import { createContext, useContext, useState, ReactNode } from 'react'

interface LayoutContextType {
  isChatOpen: boolean
  setIsChatOpen: (open: boolean) => void
  isSidebarCollapsed: boolean
  setIsSidebarCollapsed: (collapsed: boolean) => void
  canExpandSidebar: boolean
  pendingChatMessage: string | null
  setPendingChatMessage: (msg: string | null) => void
  careersReorderMode: boolean
  setCareersReorderMode: (on: boolean) => void
  careersSaveTrigger: number
  triggerCareersSave: () => void
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined)

export const LayoutProvider = ({ children }: { children: ReactNode }) => {
  const [isChatOpen, setIsChatOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [pendingChatMessage, setPendingChatMessage] = useState<string | null>(null)
  const [careersReorderMode, setCareersReorderMode] = useState(false)
  const [careersSaveTrigger, setCareersSaveTrigger] = useState(0)
  const triggerCareersSave = () => setCareersSaveTrigger(n => n + 1)

  // Auto-collapse sidebar when chat opens, restore when chat closes
  const handleChatToggle = (open: boolean) => {
    setIsChatOpen(open)
    if (open) {
      setIsSidebarCollapsed(true)
    }
    // Note: We don't auto-expand sidebar when chat closes to let user control it
  }

  // Disable sidebar expansion when chat is open
  const canExpandSidebar = !isChatOpen

  return (
    <LayoutContext.Provider
      value={{
        isChatOpen,
        setIsChatOpen: handleChatToggle,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        canExpandSidebar,
        pendingChatMessage,
        setPendingChatMessage,
        careersReorderMode,
        setCareersReorderMode,
        careersSaveTrigger,
        triggerCareersSave,
      }}
    >
      {children}
    </LayoutContext.Provider>
  )
}

export const useLayout = () => {
  const context = useContext(LayoutContext)
  if (context === undefined) {
    throw new Error('useLayout must be used within a LayoutProvider')
  }
  return context
}
